import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInitialization } from '../../context/InitializationContext';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import logoImage from '../../assets/logo.png';
import './Login.css';
import { useApi } from 'context/ApiProvider';
import { Printer } from 'models/Printer';
import { fetchAllPrinterHelpers, fetchAllPrinters,  storeDefaultPrinters,  storePrintHelpersAll, UpdateUnsavedPrintCounts } from 'services/Printerservice';
import { ConditionSide } from 'models/Insole';
import { getPrintersByUserIdList } from 'services/adminService';
import { User } from 'models/User';

interface LoginProps {
  onLoginSuccess?: (userData: any) => void; // Optional callback for successful login
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState(''); // Changed from email to username
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { progress, isInitialized } = useInitialization();
  const { isDarkMode, toggleTheme } = useTheme();
  const { login } = useAuth();
  const api = useApi();

  // useEffect(() => {
  //   console.log('Login component - Initialization state:', { progress, isInitialized });
  // }, [progress, isInitialized]);

  const getInitializationStep = () => {
    if (progress < 10) return 'Starting initialization...';
    if (progress < 25) return 'Creating worker...';
    if (progress < 30) return 'Initializing WebGL...';
    if (progress < 40) return 'Loading textures...';
    if (progress < 50) return 'Preparing worker...';
    if (progress < 100) return 'Loaded OpenCascade';
    return 'Initialization complete!';
  };

  const fetchAvailablePrinters = async (authenticatedUser: User): Promise<Printer[]> => {
    if (
      authenticatedUser.isAdmin &&
      !authenticatedUser.group?.IsAll
    ) {
      return getPrintersByUserIdList(api, [authenticatedUser.id]);
    }
    return fetchAllPrinters(api);
  }

  const loadUserDefaultPrinters = async (authenticatedUser: User) => {
    const printers = await fetchAvailablePrinters(authenticatedUser);
    const helperSetting = await fetchAllPrinterHelpers(api);
    const leftDefaultPrinter = printers.find(p => p.defaultPrinter === ConditionSide.left || p.defaultPrinter === ConditionSide.both) || null;
    const rightDefaultPrinter = printers.find(p => p.defaultPrinter === ConditionSide.right || p.defaultPrinter === ConditionSide.both) || null;
    if (leftDefaultPrinter && rightDefaultPrinter) {
      storeDefaultPrinters(leftDefaultPrinter, rightDefaultPrinter);
    }
    if (helperSetting!== null && helperSetting.length>0){
      storePrintHelpersAll(helperSetting);
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !password) {
      setError('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // console.log('Attempting login with:', username);

      const body = {
        username: username.trim(),
        password: password.trim(),
      };

      // Make the API call
      const response = await api.post('/api/auth/login', body);

      if (response.ok) {
        // Use the login function from AuthContext to properly update authentication state
        await login( response.body.token, response.body.user);

        // Call the success callback if provided
        if (onLoginSuccess) {
          onLoginSuccess(response.body);
        }
        // Navigate to home on successful login
        if (response.body.user.isAdmin === true) {
          navigate('/admin');
        } else {
          navigate('/');
        }
        
        await UpdateUnsavedPrintCounts(api);
        loadUserDefaultPrinters(new User(response.body.user));

      } else {
        setError(response?.body.message || 'Login failed.');
      }

    } catch (err: any) {
      console.error('Login failed:', err);
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    document.getElementById('username')?.focus();
  }, []);

  return (
    <div className="login-container">
      <div className="login-header">
        <button className="login-theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
          {isDarkMode ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="5"></circle>
              <line x1="12" y1="1" x2="12" y2="3"></line>
              <line x1="12" y1="21" x2="12" y2="23"></line>
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
              <line x1="1" y1="12" x2="3" y2="12"></line>
              <line x1="21" y1="12" x2="23" y2="12"></line>
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
            </svg>
          )}
        </button>
      </div>
      <div className="login-box">
        <div className="login-logo">
          <img src={logoImage} alt="OpenCascade.js Logo" className="logo-image" />
        </div>
        {/* <h2>Login to continue</h2> */}
        {/* {error && <div className="error-message">{error}</div>} */}
        {error && <h2 style={{color: 'red'}}>{error}</h2>}

        <form className="login-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              className="login-input"
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
              disabled={isLoading}
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              className="login-input"
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              disabled={isLoading}
            />
          </div>
          <button
            type="submit"
            className={`login-button ${!isInitialized ? 'initializing' : ''} btnShine`}
            disabled={isLoading || !isInitialized}
          >
            {isLoading ? (
              <span className="loading-spinner-small"></span>
            ) : !isInitialized ? (
              <div className="button-progress-container">
                <div className="button-progress-label">
                  {getInitializationStep()}
                </div>
                <div className="button-progress-bar-container">
                  <div
                    className="button-progress-bar"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
            ) : (
              'Login'
            )}
          </button>
        </form>
        <div className="signup-link-container">
          <span>Don't have an account? </span>
          <span
            className="signup-link"
            style={{ color: "#007bff", cursor: "pointer", textDecoration: "underline" }}
            onClick={() => navigate("/register")}
          >
            Sign up now
          </span>
        </div>
        {/* <div className="signup-link-container">
          <span>Visiting us at an exhibition? </span>
          <span
            className="signup-link"
            style={{ color: "#007bff", cursor: "pointer", textDecoration: "underline" }}
            onClick={() => navigate("/exhibition")}
          >
            Open exhibition demo
          </span>
        </div> */}
      </div>
    </div>
  );
};

export default Login;
